import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogTitle, DialogContent, Card,
  CardHeader, CardContent, Typography, IconButton, List, ListItem, ListItemText,
  Backdrop, CircularProgress
} from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import axios from 'src/utils/axios';

const BeratDocumentUploadDialog = ({ open, onClose, birimKodu }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [filesMarkedForDelete, setFilesMarkedForDelete] = useState([]);
  const [inputKey, setInputKey] = useState(Date.now());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e) => {
    const filesArray = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...filesArray]);
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMarkForDelete = (fileId) => {
    setFilesMarkedForDelete(prev => [...prev, fileId]);
  };

  const handleUnmarkForDelete = (fileId) => {
    setFilesMarkedForDelete(prev => prev.filter(id => id !== fileId));
  };

  const handleDownload = (doc) => {
    if (!doc?.FileData || !doc?.ContentType || !doc?.FileName) {
      alert("Geçersiz belge verisi.");
      return;
    }
    const byteCharacters = atob(doc.FileData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.ContentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", doc.FileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0 && filesMarkedForDelete.length === 0) {
      alert("Yüklenecek ya da silinecek dosya yok.");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmUpload = async () => {
    setConfirmOpen(false);
    setIsLoading(true);

    const filesToUpload = selectedFiles;
    const filesToDelete = filesMarkedForDelete;
    const documents = [];
    let filesProcessed = 0;

    if (filesToUpload.length > 0) {
      filesToUpload.forEach(file => {
        const reader = new FileReader();
        reader.onload = function (e) {
          try {
            const base64Data = e.target.result.split(',')[1];
            documents.push({
              FileName: file.name,
              ContentType: file.type,
              FileData: base64Data,
              FileId: 0
            });
          } catch {
            alert("Dosya okunamadı: " + file.name);
          }
          filesProcessed++;
          if (filesProcessed === filesToUpload.length) {
            uploadDocumentsAndDeleteFiles(documents, filesToDelete);
          }
        };
        reader.onerror = function () {
          alert("Dosya okunurken hata oluştu: " + file.name);
          filesProcessed++;
          if (filesProcessed === filesToUpload.length) {
            uploadDocumentsAndDeleteFiles(documents, filesToDelete);
          }
        };
        reader.readAsDataURL(file);
      });
    } else {
      uploadDocumentsAndDeleteFiles([], filesToDelete);
    }
  };

  const uploadDocumentsAndDeleteFiles = async (documents, filesToDelete) => {
    try {
      const allDocuments = [...documents];

      if (filesToDelete.length > 0) {
        filesToDelete.forEach(fileId => {
          allDocuments.push({
            FileName: '',
            ContentType: '',
            FileData: '',
            FileId: fileId
          });
        });
      }

      const payload = {
        KulBirimKodu: birimKodu,
        Documents: allDocuments,
        UserId: ''
      };

      const config = { headers: { 'Content-Type': 'application/json' } };
      const res = await axios.post('SetServiceBeratDocs', payload, config);

      if (res.data.ErrorCode !== "0") {
        alert("Dosya yüklenirken hata oluştu.");
        setIsLoading(false);
        return;
      }

      alert("İşlem başarılı.");
      setSelectedFiles([]);
      setFilesMarkedForDelete([]);
      setInputKey(Date.now());
      fetchUploadedDocuments();
    } catch (err) {
      console.error("İşlem hatası:", err);
      alert("İşlem sırasında hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUploadedDocuments = async () => {
    try {
      const config = { headers: { 'Content-Type': 'application/json' } };
      const res = await axios.get(`GetBeratDocuments?KulBirimKodu=${birimKodu}`, config);
      setUploadedDocs(res.data || []);
    } catch (err) {
      console.error('Dosyalar getirilemedi:', err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUploadedDocuments();
      setSelectedFiles([]);
      setFilesMarkedForDelete([]);
      setInputKey(Date.now());
    }
  }, [open, birimKodu]);

  const handleCancel = () => {
    if (isLoading) return; // Yükleme devam ederken kapatmaya izin verme
    setSelectedFiles([]);
    setFilesMarkedForDelete([]);
    setInputKey(Date.now());
    onClose();
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm" onClose={handleCancel} disableEscapeKeyDown={isLoading}>

        <DialogTitle style={{ textAlign: 'center' }}>
          Berat Belgeleri Yükleme
          <IconButton
            onClick={handleCancel}
            style={{ position: 'absolute', right: 16, top: 16 }}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers style={{ position: 'relative' }}>
          {/* Yeni Belgeler Card */}
          <Card variant="outlined" style={{ marginBottom: 16 }}>
            <CardHeader title="Yeni Belgeleri Yükle" />
            <CardContent>
              <input
                key={inputKey}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileChange}
                style={{ display: 'block', marginBottom: 8 }}
                disabled={isLoading}
              />
              {selectedFiles.length > 0 && (
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem
                      key={`new-${file.name}-${index}`}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="remove"
                          onClick={() => handleRemoveSelectedFile(index)}
                          size="small"
                          disabled={isLoading}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={file.name} secondary={file.type} />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Yüklenmiş Belgeler Card */}
          <Card variant="outlined">
            <CardHeader title="Yüklenmiş Belgeler" />
            <CardContent>
              {uploadedDocs.length > 0 ? (
                <List dense>
                  {uploadedDocs.map((doc, i) => {
                    const isMarked = filesMarkedForDelete.includes(doc.FileId);
                    return (
                      <ListItem
                        key={`uploaded-${doc.FileId}-${i}`}
                        secondaryAction={
                          <>
                            {!isMarked && (
                              <>
                                <IconButton
                                  onClick={() => handleDownload(doc)}
                                  title="İndir"
                                  disabled={isLoading}
                                >
                                  <GetAppIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleMarkForDelete(doc.FileId)}
                                  title="Silmek için işaretle"
                                  color="error"
                                  disabled={isLoading}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </>
                            )}
                            {isMarked && (
                              <IconButton
                                onClick={() => handleUnmarkForDelete(doc.FileId)}
                                title="Silme işaretini kaldır"
                                disabled={isLoading}
                              >
                                <UndoIcon />
                              </IconButton>
                            )}
                          </>
                        }
                        style={{
                          textDecoration: isMarked ? 'line-through' : 'none',
                          color: isMarked ? 'red' : 'inherit'
                        }}
                      >
                        <ListItemText primary={doc.FileName} secondary={doc.date} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Henüz belge yüklenmemiş.
                </Typography>
              )}
            </CardContent>
          </Card>
          {isLoading && (
        <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
        <CircularProgress />
      </div>
    )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} variant="outlined" color="secondary" disabled={isLoading}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            startIcon={<CloudUploadIcon />}
            disabled={(selectedFiles.length === 0 && filesMarkedForDelete.length === 0) || isLoading}
            style={{
              backgroundColor: (selectedFiles.length > 0 || filesMarkedForDelete.length > 0) ? '#f57c00' : undefined,
              color: (selectedFiles.length > 0 || filesMarkedForDelete.length > 0) ? '#fff' : undefined,
            }}
          >
            Yükle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onay Dialogu (aynı, sadece isLoading disable eklenebilir) */}
      <Dialog open={confirmOpen} onClose={() => !isLoading && setConfirmOpen(false)} disableEscapeKeyDown={isLoading}>
        <DialogTitle>İşlemi Onayla</DialogTitle>
        <DialogContent dividers>
          {selectedFiles.length > 0 && (
            <>
              <Typography variant="subtitle2">Yüklenecek Dosyalar:</Typography>
              <ul>
                {selectedFiles.map((file, i) => (
                  <li key={`upload-${i}`}>{file.name}</li>
                ))}
              </ul>
            </>
          )}
          {filesMarkedForDelete.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Silinecek Dosyalar:</Typography>
              <ul>
                {uploadedDocs
                  .filter(doc => filesMarkedForDelete.includes(doc.FileId))
                  .map((doc, i) => (
                    <li key={`delete-${i}`}>{doc.FileName}</li>
                  ))}
              </ul>
            </>
          )}
          <Typography sx={{ mt: 2 }}>Bu işlemi yapmak istediğinizden emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary" disabled={isLoading}>Hayır</Button>
          <Button onClick={handleConfirmUpload} variant="contained" color="primary" disabled={isLoading} style={{
            backgroundColor: '#f57c00',
            color: '#fff'
          }}>Evet</Button>
        </DialogActions>

        
      </Dialog>

      
    </>
  );
};


export default BeratDocumentUploadDialog;
